/**
 * DriverComplianceBanner — Phase 43.12
 *
 * Mostra al driver lo stato delle scadenze patente + assicurazione veicolo:
 *  - red    → almeno un documento scaduto → BLOCCATO (niente accept ordini)
 *  - yellow → almeno un documento scade entro 30 giorni → solo warning
 *  - missing → date non inserite → invita a compilarle
 *  - green  → tutto ok → componente nascosto (return null)
 *
 * Carica lo stato da GET /api/driver/compliance-status.
 * Usa il refresh dell'utente per riallinearsi dopo l'update.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ShieldAlert, Clock, FileWarning } from "lucide-react";
import { api } from "@/lib/api";

const STATE_LABELS = {
  ok: "Valida",
  warning: "In scadenza",
  expired: "Scaduta",
  missing: "Non inserita",
};

function fmtDays(daysLeft) {
  if (typeof daysLeft !== "number") return "—";
  if (daysLeft < 0) return `scaduta da ${Math.abs(daysLeft)} g`;
  if (daysLeft === 0) return "scade oggi";
  if (daysLeft === 1) return "scade domani";
  return `${daysLeft} giorni`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("it-CH", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function DriverComplianceBanner({ onState }) {
  const [state, setState] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/driver/compliance-status");
        if (!alive) return;
        setState(data);
        if (typeof onState === "function") onState(data);
      } catch {
        // silent: non bloccare la home se l'endpoint fallisce
      }
    })();
    return () => { alive = false; };
  }, [onState]);

  if (!state) return null;
  if (state.overall === "green") return null;

  // Stato visivo
  const isRed = state.overall === "red";
  const isYellow = state.overall === "yellow";
  const isMissing = state.overall === "missing";

  const tone = isRed
    ? "border-red-200 bg-red-50 text-red-800"
    : isYellow
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : "border-zinc-200 bg-zinc-50 text-zinc-800";
  const Icon = isRed ? ShieldAlert : isYellow ? AlertTriangle : FileWarning;
  const title = isRed
    ? "Documento scaduto · operatività bloccata"
    : isYellow
      ? "Documento in scadenza"
      : "Completa i dati di compliance";
  const description = isRed
    ? "Aggiorna patente o assicurazione per tornare operativo. Non puoi ricevere o accettare nuovi ordini finché i documenti non sono validi."
    : isYellow
      ? "Uno o più documenti scadono entro 30 giorni. Puoi ancora accettare ordini: aggiorna i dati per evitare il blocco automatico."
      : "Inserisci la scadenza della patente e dell'assicurazione veicolo per restare operativo.";

  const items = [
    { id: "license", label: "Patente", info: state.license },
    { id: "insurance", label: "Assicurazione veicolo", info: state.insurance },
  ];

  return (
    <div
      data-testid="driver-compliance-banner"
      data-compliance-state={state.overall}
      className={`rounded-2xl border p-4 sm:p-5 ${tone}`}
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/70 shrink-0">
          <Icon className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-display text-base font-bold tracking-tight">{title}</p>
          <p className="text-sm mt-1 leading-relaxed">{description}</p>

          <ul className="mt-3 space-y-1.5 text-sm" data-testid="driver-compliance-list">
            {items.map((it) => {
              const s = it.info?.state || "missing";
              const pillCls = s === "expired"
                ? "bg-red-100 text-red-800"
                : s === "warning"
                  ? "bg-amber-100 text-amber-800"
                  : s === "missing"
                    ? "bg-zinc-200 text-zinc-700"
                    : "bg-emerald-100 text-emerald-800";
              return (
                <li
                  key={it.id}
                  data-testid={`driver-compliance-item-${it.id}`}
                  className="flex items-center justify-between gap-2 flex-wrap"
                >
                  <span className="font-semibold">
                    {it.label}
                  </span>
                  <span className="inline-flex items-center gap-2 text-xs">
                    <span className="text-zinc-700">{fmtDate(it.info?.expiry)}</span>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${pillCls}`}>
                      <Clock className="h-3 w-3" />
                      {STATE_LABELS[s]} {s === "warning" || s === "expired" ? `· ${fmtDays(it.info?.days_left)}` : ""}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>

          <div className="mt-3">
            <Link
              to="/driver/vehicle"
              data-testid="driver-compliance-update-cta"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-white text-dapnow-ink font-semibold text-sm border border-black/10 hover:bg-white/90 transition"
            >
              Aggiorna documenti
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
